import { z } from 'zod'
import { DateRangeFilterSchema, UTCMillisecondsSchema, UTCSecondsSchema } from '../time'
import { RecordingIdSchema, RubricCategorySchema, RubricIdSchema } from '../recording'
import { Geo200CompanySchema } from '../locations/model'
import { LatLngSchema } from '../locations/latLng'
import {
  AccountIdSchema,
  CompanyIdSchema,
  idSchemaV2,
  isId,
  LocationIdSchema,
  TeamIdSchema,
  UserIdSchema,
  WorkflowActionIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
} from '../ids'
import { TinyUserSchema } from '../entity'
import { RangeFilterSchema } from '../customFieldMap'
import { ComplianceViolationIdSchema } from '../compliance/violation/id'
import { CompliancePolicyIdSchema } from '../compliance/policy/model'
import { AuditPropsSchema } from '../common'
import { AccountDataSchema } from '../account/model'
import { SimpleTimestampSchema } from './timestamp'
import { FeedbackSchema } from './feedback/model'
import { ConversationFlagMatchIdSchema } from './conversationFlagMatchId'

export const ConversationIdSchema = idSchemaV2('Convo')
export type ConversationId = z.infer<typeof ConversationIdSchema>
export const isConversationId = isId(ConversationIdSchema)

export const WithConversationIdSchema = z.object({
  convoId: ConversationIdSchema,
})
export type WithConversationId = z.infer<typeof WithConversationIdSchema>

export const ConversationTranscriptSchema = z.object({
  speaker: z.int().nonnegative(),
  text: z.string(),
  start: z.number().nonnegative(),
  end: z.number().nonnegative(),
})
export type ConversationTranscript = z.infer<typeof ConversationTranscriptSchema>

export const TranscriptStatsSchema = z.object({
  speaker: z.int().nonnegative(),
  wordCount: z.number().nonnegative(),
  speakingDuration: z.number().nonnegative().describe('Total speaking duration in seconds'),
})
export type TranscriptStats = z.infer<typeof TranscriptStatsSchema>

export const CONVERSATION_STATUSES = [
  'Processing',
  'Transcribed',
  'TranscribeFailed',
  'GradingSkipped',
  'Graded',
  'GradingFailed',
  'Flagged',
] as const

export const ConversationStatusSchema = z.enum(CONVERSATION_STATUSES)
export type ConversationStatus = z.infer<typeof ConversationStatusSchema>

export const ConversationCategoryTimestampsSchema = z.array(
  z.object({
    timestamp: SimpleTimestampSchema,
    chapterTitle: z.string(),
  })
)
export type ConversationCategoryTimestamps = z.infer<typeof ConversationCategoryTimestampsSchema>

export const ConversationTinyAccountSchema = AccountDataSchema.pick({
  accountId: true,
  statusId: true,
  workflowId: true,
  workflowStageId: true,
  workflowStageName: true,
  workflowActionId: true,
  workflowActionName: true,
})
export type ConversationTinyAccount = z.infer<typeof ConversationTinyAccountSchema>

export const ConversationComplianceViolationSchema = z
  .object({
    type: z.literal('Compliance'),
    violationId: ComplianceViolationIdSchema,
    policyId: CompliancePolicyIdSchema,
    policyTitle: z.string().optional(),
    timestamps: SimpleTimestampSchema.array(),
    violationDate: UTCMillisecondsSchema,
  })
  .meta({ description: 'A compliance violation detected in the conversation.' })
export type ConversationComplianceViolation = z.infer<typeof ConversationComplianceViolationSchema>

export const ConversationExistingCustomerViolationSchema = z
  .object({
    type: z.literal('ExistingCustomer'),
    violationId: ConversationFlagMatchIdSchema,
    accountId: AccountIdSchema,
    workflowStageId: WorkflowStageIdSchema,
    locationId: LocationIdSchema,
    flaggedAt: UTCMillisecondsSchema,
    resolvedBy: UserIdSchema.optional(),
    resolvedAt: UTCMillisecondsSchema.optional(),
  })
  .meta({ description: 'A recording made at a location with an existing customer in a restricted stage.' })
export type ConversationExistingCustomerViolation = z.infer<typeof ConversationExistingCustomerViolationSchema>

export const ConversationViolationSchema = z.discriminatedUnion('type', [
  ConversationComplianceViolationSchema,
  ConversationExistingCustomerViolationSchema,
])
export type ConversationViolation = z.infer<typeof ConversationViolationSchema>

export const ConversationDataSchema = z.object({
  ...AuditPropsSchema.shape,
  convoId: ConversationIdSchema,
  duration: z.number().meta({ description: 'Duration of the conversation in seconds' }),
  convoDate: UTCMillisecondsSchema,
  status: ConversationStatusSchema,
  violations: ConversationViolationSchema.array().optional().meta({
    description:
      'Compliance and related issues found in this conversation. Returned by conversation/get and conversation/list for callers with company-level conversation:manage; omitted otherwise.',
  }),
  recordingId: RecordingIdSchema.optional(),
  ownerId: UserIdSchema,
  owner: TinyUserSchema.optional(),
  companyId: CompanyIdSchema,
  latlng: LatLngSchema.optional(),
  locationId: LocationIdSchema.optional(),
  geo200Company: Geo200CompanySchema.optional(),
  accountId: AccountIdSchema.optional(),
  account: ConversationTinyAccountSchema.optional(),

  workflowId: WorkflowIdSchema.optional(),
  workflowStageId: WorkflowStageIdSchema.optional(),
  workflowActionId: WorkflowActionIdSchema.optional(),
  workflowLastActionDate: UTCMillisecondsSchema.optional(),

  transcript: ConversationTranscriptSchema.array().optional(),
  /**
   * @deprecated all conversations are pitches
   */
  isPitch: z.boolean().optional(),
  downloadUrl: z.string().optional(),
  originalRubricId: RubricIdSchema.optional().meta({
    description: `The ID of the rubric used to originally grade the conversation.
                  Do not use for displaying the grade.`,
  }),
  rubric: RubricCategorySchema.array().optional(),
  assigneeId: UserIdSchema.optional().meta({ description: 'The ID of the user assigned to evaluate the conversation' }),
  assignee: TinyUserSchema.optional(),
  feedback: z.array(FeedbackSchema).optional().meta({ description: 'List of Feedback provided for the conversation' }),
  feedbackSummary: z.string().array().optional(),
  skillShare: z.string().array().optional().meta({ description: 'Conversations with these categories are shareable' }),
  aiSkillShare: z.boolean().optional().meta({ description: 'Whether or not the skill share came from AI' }),
  skillShareValidAfter: UTCMillisecondsSchema.optional().meta({
    description: 'Timestamp after which this conversation is available in Skill Share',
  }),
  scoreSummary: z.string().array().optional().meta({ description: 'Bullet points summarizing the feedback' }),
  notes: z.string().optional().meta({ description: 'Additional notes about the conversation' }),
  categoryTimestamps: ConversationCategoryTimestampsSchema.optional().meta({
    description: 'List of timestamps for each category mentioned in the conversation',
  }),
  transcriptStats: TranscriptStatsSchema.array().optional().meta({
    description: 'Statistics about each speaker in the conversation',
  }),
  expiresAt: UTCSecondsSchema.optional(),
  address: z.string().optional().meta({ description: 'Full address of the conversation location, decorated field' }),
  score: z.number().optional().meta({
    description:
      'AI score as a percentage from 0 to 100, decorated field. Missing when the conversation is not graded.',
  }),
})
export type ConversationData = z.infer<typeof ConversationDataSchema>

export const ConversationSortBySchema = z.enum(['date', 'score', 'duration', 'owner']).meta({
  description: 'Field to sort conversations by.',
})
export type ConversationSortBy = z.infer<typeof ConversationSortBySchema>

export const ConversationSortOrderSchema = z.enum(['asc', 'desc']).meta({
  description: 'Sort direction.',
})
export type ConversationSortOrder = z.infer<typeof ConversationSortOrderSchema>

export const ConversationFilterSchema = z.object({
  statuses: ConversationStatusSchema.array().optional().meta({
    description: 'Conversation statuses to include.',
  }),
  query: z.string().nonempty().optional().meta({ description: 'Text matched against owner, assignee and address' }),
  ownerIds: z.array(UserIdSchema).optional().meta({ description: 'Only conversations recorded by these users' }),
  ownerTeamIds: z.array(TeamIdSchema).optional().meta({ description: 'Only conversations recorded by these teams' }),
  assigneeIds: z
    .array(UserIdSchema)
    .optional()
    .meta({ description: 'Only conversations assigned to these evaluators' }),
  assigneeTeamIds: z
    .array(TeamIdSchema)
    .optional()
    .meta({ description: 'Only conversations whose evaluator is on these teams' }),
  date: DateRangeFilterSchema.optional().meta({ description: 'Conversation date range in UTC milliseconds' }),
  duration: RangeFilterSchema.optional().meta({ description: 'Duration range in seconds' }),
  score: RangeFilterSchema.optional().meta({
    description: 'AI score range as a percentage from 0 to 100. Any score filter leaves out ungraded conversations.',
  }),
  workflowId: WorkflowIdSchema.optional().meta({ description: 'Only conversations classified into this workflow' }),
  actionIds: z
    .array(WorkflowActionIdSchema)
    .optional()
    .meta({ description: 'Only conversations classified as these workflow actions' }),
  stageIds: z
    .array(WorkflowStageIdSchema)
    .optional()
    .meta({ description: 'Only conversations classified as these workflow stages' }),
  classified: z
    .boolean()
    .optional()
    .meta({ description: 'Only conversations with (true) or without (false) a workflow' }),
})
export type ConversationFilter = z.infer<typeof ConversationFilterSchema>
